import { UserManager, UserManagerSettings, User } from 'oidc-client-ts';

import config from '../tidConfig.json';
import { getEnv } from '../utils/environmentUtils';

const env = getEnv();
const { clientId, openIdConfigUrl } = config[env];

const AuthService: {
    userManager: UserManager | null;
    user: User | null;
    baseUrl: string;
    authority: string;
    handleError: (error: unknown) => void;
    initialize: () => void;
    isAuthenticated: () => boolean;
    getUser: () => Promise<User | null>;
    login: () => void;
    loginRedirect: () => Promise<User | null>;
    silentSignInCallback: () => void;
    logout: () => void;
    completeLogout: () => Promise<void>;
} = {
    userManager: null,
    user: null,
    baseUrl: window.location.origin,
    authority: openIdConfigUrl,
    handleError: (error: unknown): void => {
        console.error('Problem with authentication endpoint: ', error);
    },
    initialize: (): void => {
        const settings: UserManagerSettings = {
            client_id: clientId,
            authority: AuthService.authority,
            post_logout_redirect_uri: `${AuthService.baseUrl}/revoke`,
            silent_redirect_uri: `${AuthService.baseUrl}`,
            redirect_uri: `${AuthService.baseUrl}/authorize`,
            response_type: 'code',
            scope: 'openid network-insights',
            validateSubOnSilentRenew: true,
            includeIdTokenInSilentRenew: false,
            monitorSession: true,
            automaticSilentRenew: true,
        };

        AuthService.userManager = new UserManager(settings);
        AuthService.userManager.events.removeAccessTokenExpired((): void => {
            AuthService.logout();
        });
    },
    isAuthenticated: (): boolean => {
        return AuthService.user !== null && AuthService.user.access_token !== '' && !AuthService.user.expired;
    },
    getUser: (): Promise<User | null> => {
        if (AuthService.isAuthenticated()) {
            return new Promise((resolve): void => {
                resolve(AuthService.user);
            });
        }
        if (AuthService.userManager !== null) {
            return AuthService.userManager.getUser()
                .then((response): User | null => {
                    AuthService.user = response;
                    return AuthService.user;
                })
                .catch((error): null => {
                    AuthService.handleError(error);
                    return null;
                });
        }
        return Promise.resolve(null);
    },
    login: (): void => {
        if (AuthService.userManager !== null) {
            AuthService.userManager.signinRedirect()
                .catch((error): void => {
                    AuthService.handleError(error);
                });
        }
    },
    loginRedirect: (): Promise<User | null> => {
        if (AuthService.userManager !== null) {
            return AuthService.userManager.signinRedirectCallback()
                .then((response): User => {
                    AuthService.user = response;
                    return AuthService.user;
                })
                .catch((error): null => {
                    AuthService.handleError(error);
                    return null;
                });
        }
        return Promise.resolve(null);
    },
    silentSignInCallback(): void {
        if (AuthService.userManager != null) {
            AuthService.userManager.signinSilentCallback();
        }
    },
    logout: (): void => {
        if (AuthService.userManager !== null) {
            AuthService.userManager.signoutRedirect();
        }
    },
    completeLogout: (): Promise<void> => {
        if (AuthService.userManager !== null) {
            return AuthService.userManager.signoutRedirectCallback()
                .then(() => {
                    if (AuthService.userManager !== null) {
                        AuthService.userManager.removeUser();
                        AuthService.userManager.clearStaleState();
                        AuthService.user = null;
                        // redirect to login
                        window.location.href = AuthService.baseUrl;
                    }
                })
                .catch((error): void => { AuthService.handleError(error); });
        }
        return Promise.resolve();
    },
};

export default AuthService;
